import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Popover, PopoverBody } from 'reactstrap';

import { UserAvatar } from '@techchange/filestack';
import { messages } from '../../../../../../_shared/i18n/messages';

import { insertMention } from '../commands';
import { mentionTriggerPluginKey } from '../plugins/mentionTrigger';

import './MentionDropdown.scss';

const PAGE_SIZE = 15;

const propTypes = {

	/**
	 * The users that are available to at-mention.
	 *
	 * NOTE: Map from userID -> user, enrollment, and avatar data.
	 */
	mentionUsers: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The user id.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user display name.
		 */
		display_name: PropTypes.string.isRequired,

		/**
		 * An array of avatars (profile pictures).
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			image: PropTypes.string,

			signature: PropTypes.string,

			policy: PropTypes.string,

		})).isRequired,

	})).isRequired,

	/**
	 * Flag specifying if chat view should be rendered.
	 */
	chatView: PropTypes.bool.isRequired,

	/**
	 * Flag specifying if mobile view should be rendered.
	 */
	mobileView: PropTypes.bool.isRequired,

	/**
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

};

const MentionDropdown = (props) => {

	const {
		mentionUsers,
		chatView,
		mobileView,
		editorView,
	} = props;

	const intl = useIntl();

	// Get plugin state from current view
	const pluginState = mentionTriggerPluginKey.getState(editorView.state);
	const { isMentionActive, mentionStart, mentionEnd, queryText } = pluginState;

	// Get DOM element of mention being typed
	const mentionDOM = isMentionActive && editorView.nodeDOM(mentionStart);

	// Build the list of user options to at-mention from the dropdown
	const userOptions = useMemo(() => {

		const result = [];

		if (mentionUsers) {

			for (const key in mentionUsers) {

				if (mentionUsers.hasOwnProperty(key)) {

					const user = mentionUsers[key];

					if (user) {

						result.push({
							...user,
							label: user.display_name,
							value: user.display_name,
						});

					}

				}

			}

		}

		return result;

	}, [mentionUsers]);

	// Enable editor to keep track of reference to react-select component
	const selectRef = useCallback((selectNode) => {

		const tr = editorView.state.tr.setMeta('selectRef', selectNode);

		editorView.dispatch(tr);

	}, [editorView]);

	const handleChange = (option, { action }) => {

		if (action === 'select-option') {

			const optionData = {
				id: option.id,
				username: option.display_name,
			};

			insertMention(optionData, mentionStart, mentionEnd)(editorView.state, editorView.dispatch);

		}

	};

	const loadOptions = (searchText, prevOptions) => {

		let filteredOptions = userOptions;

		// Filter options if non-empty search query
		if (searchText && searchText !== '') {

			filteredOptions = userOptions.filter((option) => (
				option.display_name.toLowerCase().includes(searchText.toLowerCase())
			));

		}

		const nextOptions =
			filteredOptions.slice(prevOptions.length, prevOptions.length + PAGE_SIZE);

		const hasMore = filteredOptions.length > (prevOptions.length + PAGE_SIZE);

		return {
			options: nextOptions,
			hasMore,
		};

	};

	const formatUserOption = (option) => {

		return (
			<div className="d-flex align-items-center">
				<UserAvatar
					className="me-2"
					user={option}
					size="30"
					rounded />
				<div className="user-handle">
					{option.display_name}
				</div>
			</div>
		);

	};

	if (isMentionActive && mentionDOM?.parentElement) {

		return (
			<Popover
				key={queryText}
				className={`tc-pm-mention-dropdown ${mobileView ? 'mobile' : ''}`}
				target={mentionDOM?.parentElement}
				placement={(chatView || mobileView) ? 'top-start' : 'bottom-start'}
				fade={false}
				hideArrow
				isOpen>
				<PopoverBody className="p-0">
					<AsyncPaginate
						selectRef={selectRef}
						id="mention-select"
						styles={{
							control: (styles) => {
								return Object.assign({}, styles, {
									// Hide the react-select input
									display: 'none',
								});
							},
							container: (styles) => {
								return Object.assign({}, styles, {
									// Make sure a width is set on the select menu
									width: '100%',
								});
							},
							menu: (styles) => {
								return Object.assign({}, styles, {
									// Let Popper parent handle positioning
									position: 'unset',
									margin: 0,
								});
							},
							menuList: (styles) => {
								return Object.assign({}, styles, {
									// Override default max-height for mobile
									maxHeight: mobileView ? '150px' : '300px',
								});
							},
							option: (styles) => {
								return Object.assign({}, styles, {
									cursor: 'pointer',
								});
							},
						}}
						inputValue={queryText}
						noOptionsMessage={
							() => intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_MENTION_DROPDOWN_EMPTY_STATE)
						}
						formatOptionLabel={formatUserOption}
						defaultOptions={userOptions.slice(0, PAGE_SIZE)}
						loadOptions={loadOptions}
						debounceTimeout={350}
						menuIsOpen
						onChange={handleChange} />
				</PopoverBody>
			</Popover>
		);

	}

	return <div />;

};

MentionDropdown.propTypes = propTypes;

export default MentionDropdown;
